import type { AcpUsageData } from '@crowdex/agent-contracts';
import {
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTools,
  usePromptInputAttachments,
} from '@crowdex/ui/ai/prompt-input';
import {
  DesktopDropdownContent,
  DesktopDropdownLabel,
  DesktopDropdownRadioItem,
} from '@crowdex/ui/components/desktop';
import {
  DropdownMenu,
  DropdownMenuRadioGroup,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { LoaderCircle, Zap } from 'lucide-react';
import { type ComponentProps, type ReactNode, type Ref, useEffect, useRef, useState } from 'react';
import { useComposerAgentSwitch } from '@/components/agent-composer-controls';
import {
  type AcpSelectConfigOption,
  flattenConfigOptions,
  ModelConfigMenu,
  type PlanUsage,
} from '@/components/desktop-config-controls';
import { useComposerMenuSide } from '@/components/desktop-shell/composer-menu-side';
import type { PickerOpenScope } from '@/components/desktop-shell/composer-picker';
import {
  ComposerPlusMenu,
  type ComposerPlusMenuHandle,
} from '@/components/desktop-shell/composer-plus-menu';
import type { MentionTarget } from '@/components/desktop-side-panel';
import { usePluginConversationComposer } from '@/features/plugins/conversation-composer-context';
import { LiveCallButton, useLiveCallAvailable } from '@/features/voice-call/live-call';
import { useAccountAgentProfiles } from '@/hooks/use-account-agent-profiles';
import { useAiAgentSelection } from '@/hooks/use-conversation-settings';
import { savedAgentParticipant } from '@/lib/agent-runtime/saved-agents';
import { agentsForRuntime } from '@/lib/desktop-shell/cloud-agents';
import {
  nextPermissionValue,
  PERMISSIONS_SHORTCUT,
  permissionName,
  permissionOption,
} from '@/lib/desktop-shell/composer-permissions';
import type { RepositoryFile } from '@/lib/desktop-shell/repository-files';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import type { AvailablePluginSkill } from '@/lib/native-agent/types';
import type { NativeAgent } from '@/lib/native-desktop';
import { matchesShortcut, shortcutLabel } from '@/lib/platform';
import { commandLabel, currentShortcut } from '@/lib/settings-page/keyboard-shortcuts';

export type ComposerToolbarProps = {
  agent: NativeAgent | null;
  agentId: string;
  agents: readonly NativeAgent[];
  agentDiscoveryError: string | null;
  selectAgent: (nextId: string) => Promise<void>;
  agentRuntime: 'cloud' | 'local';
  busy: boolean;
  /** What the +'s search finds among the references (@'s). */
  mentionTargets: readonly MentionTarget[];
  cloudHandoffWorking: boolean;
  localHandoffWorking: boolean;
  /** The agent's plan mode, when it offers one. */
  planFirst: { enabled: boolean } | null;
  changePlanFirst: (enabled: boolean) => void;
  changeModelConfig: (configId: string, value: string) => Promise<void>;
  insertArtifact: (target: MentionTarget) => void;
  insertMention: (target: MentionTarget) => void;
  insertSkill: (skill: AvailablePluginSkill) => void;
  modelConfigOptions: readonly AcpSelectConfigOption[];
  skills: readonly AvailablePluginSkill[];
  status: ComponentProps<typeof PromptInputSubmit>['status'];
  stop: () => void;
  canSubmit: boolean;
  canSteer: boolean;
  composerText: string;
  pluginScope?: string;
  usage: AcpUsageData | null;
  /** The account plan's period, when the agent reports it (the model popover shows it). */
  planUsage?: PlanUsage | null;
  /** Show the agent choices in the model popover. Off for secondary panes (agent is workspace-level). */
  showAgentPicker?: boolean;
  /** The dictation microphone and the plugins' composer actions, before the send button. */
  pluginActions?: ReactNode;
  /** Live (the filled button while nothing is typed) cannot start now. */
  liveDisabled?: boolean;
  /** Where the conversation runs, and the dialog that changes it. */
  environment?: { label: string; onOpen: (() => void) | undefined };
  /** Opens / or @ in the composer (the + scoped: Mention a file, All skills, ⌘⇧A). */
  onOpenScope?: (scope: PickerOpenScope) => void;
  /** Writes files of the repository into the draft (`@path`). */
  insertFiles?: (files: readonly RepositoryFile[]) => void;
  /** The + itself, opened by Backspace out of / or @. */
  plusMenuRef?: Ref<ComposerPlusMenuHandle>;
  /** Whether the +'s search finds the repository's files. */
  repositoryFiles?: { preview: boolean } | undefined;
  /**
   * A new conversation's setup chips (D1): they follow +, the model and the permissions, so the
   * composer has one row of chips with the microphone and the send button, and nothing filled
   * while it is empty.
   */
  leading?: ReactNode;
  /** A floating composer's "to <conversation>" chip (D60), first on the tools' row. */
  target?: ReactNode;
};

/**
 * The permissions of the conversation, always in sight ("Auto"): its choices, and ⇧Tab from the
 * composer to go to the next.
 */
function PermissionsChip({
  disabled,
  onChange,
  option,
}: {
  disabled: boolean;
  onChange: (configId: string, value: string) => void;
  option: AcpSelectConfigOption;
}) {
  const menuSide = useComposerMenuSide();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          aria-label={`Permissions: ${permissionName(option)}`}
          // A narrow composer keeps its icon whole rather than sliding it under the microphone. On
          // a phone the permissions are the +'s row (mobile 5); a narrow window keeps the chip.
          className="type-meta flex h-8 min-w-8 shrink-0 items-center justify-center max-sm:pointer-coarse:hidden gap-1.5 rounded-control px-2.5 text-muted-foreground transition-colors hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 @max-[24rem]/composer-footer:px-0"
          title={`Permissions (${commandLabel('permissions', PERMISSIONS_SHORTCUT)} to change)`}
          type="button"
        >
          <Zap className="size-3.5 shrink-0" />
          <span className="truncate @max-[24rem]/composer-footer:hidden">
            {permissionName(option)}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DesktopDropdownContent align="start" className="min-w-52" side={menuSide} sideOffset={8}>
        <DesktopDropdownLabel className="py-1 font-normal">Permissions</DesktopDropdownLabel>
        <DropdownMenuRadioGroup
          onValueChange={(value) => onChange(option.id, value)}
          value={option.currentValue}
        >
          {flattenConfigOptions(option).map((choice) => (
            <DesktopDropdownRadioItem
              key={choice.value}
              title={choice.description}
              value={choice.value}
            >
              {choice.name}
            </DesktopDropdownRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DesktopDropdownContent>
    </DropdownMenu>
  );
}

/**
 * The composer's toolbar: on the left, + (D7), the model chip (D8) and the permissions; on the
 * right, the microphone and one filled button: Live while nothing is typed, send once something
 * is, stop while the agent works.
 */
export function ComposerToolbar({
  agent,
  agentId,
  agents,
  agentDiscoveryError,
  selectAgent,
  agentRuntime,
  busy,
  mentionTargets,
  cloudHandoffWorking,
  localHandoffWorking,
  planFirst,
  changePlanFirst,
  changeModelConfig,
  insertArtifact,
  insertMention,
  insertSkill,
  modelConfigOptions,
  skills,
  status,
  stop,
  canSubmit,
  canSteer,
  composerText,
  pluginScope = 'default',
  usage,
  planUsage = null,
  showAgentPicker = true,
  pluginActions,
  liveDisabled = false,
  environment,
  onOpenScope,
  insertFiles,
  plusMenuRef,
  repositoryFiles,
  leading,
  target,
}: ComposerToolbarProps) {
  const aiAgent = useAiAgentSelection(pluginScope);
  const { profiles } = useAccountAgentProfiles();
  const [selectingProfile, setSelectingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const modelMenuOpen = useDesktopShellStore((state) => state.modelMenuOpen);
  const setModelMenuOpen = useDesktopShellStore((state) => state.setModelMenuOpen);
  const toolsRef = useRef<HTMLSpanElement>(null);
  const profileAgents = agentsForRuntime(agents, agentRuntime).filter(
    (item) => showAgentPicker || item.id === agentId,
  );
  const groupComposer = usePluginConversationComposer();
  const soloComposer = groupComposer === null;
  const attachments = usePromptInputAttachments();
  const liveAvailable = useLiveCallAvailable();
  const selectedProfile = aiAgent.profile;
  const agentSwitch = useComposerAgentSwitch({ conversationId: pluginScope, agentId, selectAgent });
  const hasDraft = composerText.trim().length > 0 || attachments.files.length > 0;
  const permissions = soloComposer ? permissionOption(modelConfigOptions) : undefined;
  const switching = selectingProfile || cloudHandoffWorking || localHandoffWorking;
  const changeAgent = (id: string) => {
    setProfileError(null);
    agentSwitch.switchAgent(id);
  };

  // ⇧Tab from the composer goes to the next permissions.
  useEffect(() => {
    if (permissions === undefined || busy) return;
    function onKeyDown(event: KeyboardEvent) {
      const keys = currentShortcut('permissions') ?? PERMISSIONS_SHORTCUT;
      if (event.defaultPrevented || !matchesShortcut(event, keys)) return;
      const composer = toolsRef.current?.closest('.desktop-composer');
      const active = document.activeElement;
      if (!composer || !(active instanceof Node) || !composer.contains(active)) return;
      // A suggestion menu (/, @, +) keeps its own keys.
      if (permissions === undefined || composer.querySelector('[data-composer-menu]') !== null)
        return;
      const next = nextPermissionValue(permissions);
      if (next === null) return;
      event.preventDefault();
      void changeModelConfig(permissions.id, next);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [busy, changeModelConfig, permissions]);

  // Live is the filled button while nothing is typed (D5); otherwise (a draft, the agent working)
  // it stays one click away beside the microphone. A new conversation's composer (D1) has nothing
  // filled until something is typed: its screen's one filled button is a Needs you card's.
  const startScreen = leading !== undefined;
  const liveIsPrimary = !busy && !hasDraft && soloComposer && liveAvailable && !startScreen;
  const primary = busy ? (
    hasDraft ? (
      <PromptInputSubmit
        disabled={!canSteer}
        title={`Queue message · ${shortcutLabel({ key: 'Enter', commandOrControl: true, shift: true })} to send now`}
      />
    ) : (
      <PromptInputSubmit
        aria-label="Stop"
        onClick={(event) => {
          event.preventDefault();
          stop();
        }}
        status={status}
        title="Stop the current turn"
        type="button"
        variant="secondary"
      />
    )
  ) : liveIsPrimary ? (
    <LiveCallButton disabled={liveDisabled} variant="primary" />
  ) : (
    // Neutral while there is nothing to send, filled once there is. Nothing runs here: it is a
    // send button even after a failed turn (its ✕ read as a stuck Stop), the failure is a notice.
    <PromptInputSubmit disabled={!canSubmit} variant={hasDraft ? 'default' : 'secondary'} />
  );

  return (
    <PromptInputFooter className="@container/composer-footer px-3 pb-2.5">
      {/* The send controls on the right keep their size; the tools give way on narrow composers. */}
      <PromptInputTools
        className={`min-w-0 gap-1.5 overflow-hidden ${leading !== undefined ? 'flex-1 flex-wrap' : ''}`}
      >
        <span hidden ref={toolsRef} />
        {target}
        <ComposerPlusMenu
          disabled={busy}
          insertArtifact={insertArtifact}
          insertFiles={insertFiles}
          insertMention={insertMention}
          insertSkill={insertSkill}
          modelOptions={soloComposer ? modelConfigOptions : []}
          onChangeModel={(configId, value) => void changeModelConfig(configId, value)}
          onOpenScope={onOpenScope}
          ref={plusMenuRef}
          repositoryFiles={repositoryFiles}
          settings={
            soloComposer
              ? {
                  // The agent is the model popover's first choice (who runs it): not in +.
                  // A new conversation's Where it runs is its repository chip: one entry.
                  environment: startScreen ? undefined : environment,
                  planFirst:
                    planFirst === null
                      ? undefined
                      : { enabled: planFirst.enabled, onChange: changePlanFirst },
                }
              : {}
          }
          skills={skills}
          targets={mentionTargets}
        />
        {soloComposer && (
          <ModelConfigMenu
            aiAgent={{
              profiles,
              agents: profileAgents,
              selected: selectedProfile,
              disabled: switching,
              onChange: (profile) => {
                setProfileError(null);
                if (!profile) {
                  aiAgent.select(null);
                  return;
                }
                const resolved = savedAgentParticipant(profile, profileAgents, 'composer');
                if (!resolved) return;
                if (
                  agentRuntime === 'cloud' &&
                  (profile.mcpServerNames?.length || profile.skillRefs?.length)
                ) {
                  setProfileError('This AI agent requires local tools.');
                  return;
                }
                setSelectingProfile(true);
                void (async () => {
                  if (resolved.agentId !== agentId) await selectAgent(resolved.agentId);
                  aiAgent.select(profile);
                })()
                  .catch((error: unknown) =>
                    setProfileError(error instanceof Error ? error.message : String(error)),
                  )
                  .finally(() => setSelectingProfile(false));
              },
            }}
            agent={
              showAgentPicker
                ? {
                    agents,
                    disabled: switching,
                    discoveryError: agentDiscoveryError,
                    id: agentId,
                    name: agent?.name ?? null,
                    onChange: changeAgent,
                    runtime: agentRuntime,
                  }
                : undefined
            }
            disabled={busy}
            onChange={(configId, value) => void changeModelConfig(configId, value)}
            onOpenChange={setModelMenuOpen}
            open={modelMenuOpen}
            // The permissions have their own chip; Plan mode is in +.
            options={modelConfigOptions.filter((option) => option !== permissions)}
            planUsage={planUsage}
            usage={usage}
          />
        )}
        {permissions !== undefined && (
          <PermissionsChip
            disabled={busy}
            onChange={(configId, value) => void changeModelConfig(configId, value)}
            option={permissions}
          />
        )}
        {leading !== undefined && (
          // A new conversation's chips follow the tools in the same row (D1).
          <div className="contents [&>fieldset]:contents" data-composer-setup>
            {leading}
          </div>
        )}
        {(profileError ?? agentSwitch.error) && (
          <span role="alert" className="type-caption text-destructive">
            {profileError ?? agentSwitch.error}
          </span>
        )}
        {busy && (
          <span className="type-caption flex items-center gap-1.5 text-muted-foreground">
            <LoaderCircle className="size-3.5 animate-spin" />
          </span>
        )}
      </PromptInputTools>
      <div className="flex shrink-0 items-center gap-1.5">
        {soloComposer && !liveIsPrimary && <LiveCallButton disabled={liveDisabled} />}
        {pluginActions}
        {groupComposer?.recipientControl}
        {primary}
      </div>
    </PromptInputFooter>
  );
}
